import React, { useCallback, useRef, useState } from 'react'
import { FloatingMenu } from '../../FloatingMenu/FloatingMenu'
import { FLOATING_MENU_PLACEMENT } from '../../FloatingMenu/FloatingMenu.constants'
import { FlexAlign, FlexColumn } from '../../Flex'
import {
  PopupButtonContainer,
  StyledEditIcon,
  PopupButtonLabel,
  PopupMenuContent,
  PopupMenuOption,
  ChevronIcon,
} from './PopupButton.styles'

export interface PopupButtonProps {
  label: string
  menu: JSX.Element
  openIcon?: React.ReactNode
  closeIcon?: React.ReactNode
  noBorder?: boolean
  closeOnSelect?: boolean
}

export const ControlledPopupButton = ({
  label,
  isOpen,
  menu,
  onClick,
  onClose,
  isDisabled,
  openIcon,
  closeIcon,
  noBorder,
  closeOnSelect,
  setIsOpen,
}: {
  label: string
  menu: JSX.Element
  isOpen: boolean
  onClick: () => void
  onClose: () => void
  isDisabled?: boolean
  openIcon?: React.ReactNode
  closeIcon?: React.ReactNode
  noBorder?: boolean
  closeOnSelect?: boolean
  setIsOpen?: (isOpen: boolean) => void
}): JSX.Element => {
  const buttonRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const onSelect = useCallback(() => {
    if (closeOnSelect) {
      setIsOpen?.(false)
    }
  }, [closeOnSelect, setIsOpen])

  return (
    <>
      <PopupButtonContainer
        className="floating-menu-button"
        onClick={() => {
          if (!isDisabled) {
            onClick()
          }
        }}
        ref={buttonRef}
        $isDisabled={isDisabled}
        $noBorder={noBorder}
      >
        <PopupButtonLabel>{label}</PopupButtonLabel>
        {!isDisabled && !openIcon && !closeIcon && <StyledEditIcon />}
        {openIcon && isOpen && openIcon}
        {closeIcon && !isOpen && closeIcon}
      </PopupButtonContainer>
      {isOpen && (
        <FloatingMenu
          isOpen={isOpen}
          onClose={onClose}
          menuRef={menuRef}
          placement={FLOATING_MENU_PLACEMENT.BOTTOM_START}
          anchorElement={buttonRef.current}
          onClick={onSelect}
        >
          {menu}
        </FloatingMenu>
      )}
    </>
  )
}

export const PopupButton = ({
  label,
  menu,
  openIcon,
  closeIcon,
  noBorder,
  closeOnSelect,
}: PopupButtonProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  return (
    <ControlledPopupButton
      label={label}
      menu={menu}
      isOpen={isOpen}
      onClick={() => setIsOpen(true)}
      onClose={onClose}
      openIcon={openIcon}
      closeIcon={closeIcon}
      noBorder={noBorder}
      closeOnSelect={closeOnSelect}
      setIsOpen={setIsOpen}
    />
  )
}

export type PopupMenuItem<T> = {
  key: string
  value: T
  background?: string
}

export type PopupButtonMenuProps<T> = {
  label: string
  items: PopupMenuItem<T>[]
  onSelect: (value: T) => void
  noBorder?: boolean
  closeOnSelect?: boolean
}

export const PopupButtonMenu = <T,>({
  label,
  items,
  onSelect,
  noBorder,
  closeOnSelect,
}: PopupButtonMenuProps<T>): JSX.Element => {
  return (
    <PopupButton
      label={label}
      openIcon={<ChevronIcon $open={true} />}
      closeIcon={<ChevronIcon $open={false} />}
      noBorder={noBorder}
      closeOnSelect={closeOnSelect}
      menu={
        <PopupMenuContent>
          <FlexColumn
            justify={FlexAlign.FLEX_START}
            align={FlexAlign.FLEX_START}
            gap={8}
          >
            {items.map((item) => (
              <PopupMenuOption
                key={item.key}
                background={item.background}
                onClick={() => onSelect(item.value)}
              >
                {item.key}
              </PopupMenuOption>
            ))}
          </FlexColumn>
        </PopupMenuContent>
      }
    />
  )
}
